import { useState, useRef, type CSSProperties, type DragEvent } from 'react';

type Phase = 'entry' | 'lore' | 'lesson' | 'quiz-prompt' | 'quiz' | 'complete';
type JumpSection = 'entry' | 'lore' | `tab-${number}` | 'quiz-prompt' | 'quiz' | 'complete';

type QuizCardId = 'introduction' | 'evidence1' | 'evidence2' | 'evidence3' | 'conclusion';

const COLORS = {
  bg: '#1a1a1a',
  panel: '#2a2a2a',
  text: '#d0d0d0',
  textMuted: '#8a8a8a',
  border: '#5a5a5a',
  borderLight: '#d0d0d0',
  stampRed: '#8b3a3a',
  stampGreen: '#2d5016',
  shadow: 'rgba(0, 0, 0, 0.4)',
} as const;

const FONTS = {
  mono: "'Courier Prime', 'Courier New', monospace",
  body: "'Crimson Text', Georgia, serif",
} as const;

const LESSON_TABS = [
  { id: 1, label: 'Overview', video: 'module1-001.mp4', title: 'Paper structure overview' },
  { id: 2, label: 'Introduction Overview', video: 'module1-002.mp4', title: 'What is an introduction' },
  { id: 3, label: 'Hook', video: 'module1-003.mp4', title: 'Hook — grab attention' },
  { id: 4, label: 'Context', video: 'module1-004.mp4', title: 'Context — explain the situation' },
  { id: 5, label: 'Thesis', video: 'module1-005.mp4', title: 'Thesis — state your claim' },
  { id: 6, label: 'Evidence', video: 'module1-006.mp4', title: 'Evidence — prove your thesis' },
  { id: 7, label: 'Conclusion', video: 'module1-007.mp4', title: 'Conclusion — wrap up' },
  { id: 8, label: 'End of Lesson', video: 'module1-008.mp4', title: 'Why the conclusion matters' },
] as const;

const QUIZ_CARDS: { id: QuizCardId; label: string }[] = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'evidence1', label: 'Evidence 1' },
  { id: 'evidence2', label: 'Evidence 2' },
  { id: 'evidence3', label: 'Evidence 3' },
  { id: 'conclusion', label: 'Conclusion' },
];

const CORRECT_ORDER: QuizCardId[] = ['introduction', 'evidence1', 'evidence2', 'evidence3', 'conclusion'];

const JUMP_SECTIONS: { value: JumpSection; label: string }[] = [
  { value: 'entry', label: 'Entry' },
  { value: 'lore', label: 'Lore' },
  ...LESSON_TABS.map((tab, index) => ({
    value: `tab-${index}` as JumpSection,
    label: `Tab ${tab.id}: ${tab.label}`,
  })),
  { value: 'quiz-prompt', label: 'Quiz Prompt' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'complete', label: 'Complete' },
];

const shuffle = <T,>(arr: T[]): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const buttonStyle: CSSProperties = {
  fontFamily: FONTS.mono,
  fontSize: '0.8rem',
  letterSpacing: '0.1em',
  color: COLORS.text,
  backgroundColor: COLORS.panel,
  border: `1px solid ${COLORS.border}`,
  padding: '12px 20px',
  minHeight: '48px',
  minWidth: '120px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textTransform: 'uppercase',
};

const Module1 = () => {
  const loreVideoRef = useRef<HTMLVideoElement>(null);
  const lessonVideoRef = useRef<HTMLVideoElement>(null);

  const [phase, setPhase] = useState<Phase>('entry');
  const [fadeOut, setFadeOut] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [videoKey, setVideoKey] = useState(0);
  const [videoCollapsed, setVideoCollapsed] = useState(false);
  const [quizSlots, setQuizSlots] = useState<(QuizCardId | null)[]>([null, null, null, null, null]);
  const [quizPool, setQuizPool] = useState<QuizCardId[]>([]);
  const [quizFeedback, setQuizFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [draggedCard, setDraggedCard] = useState<QuizCardId | null>(null);

  const currentTab = LESSON_TABS[activeTab];
  const isLastTab = activeTab === LESSON_TABS.length - 1;

  const bumpVideoKey = () => setVideoKey((k) => k + 1);

  const handleTabClick = (index: number) => {
    if (index === activeTab) {
      setVideoCollapsed((collapsed) => !collapsed);
      return;
    }
    lessonVideoRef.current?.pause();
    setActiveTab(index);
    setVideoCollapsed(false);
    bumpVideoKey();
  };

  const handleNextTab = () => {
    if (activeTab < LESSON_TABS.length - 1) {
      handleTabClick(activeTab + 1);
      return;
    }
    setPhase('quiz-prompt');
  };

  const handleBackTab = () => {
    if (activeTab > 0) {
      handleTabClick(activeTab - 1);
    }
  };

  const handleLessonBack = () => {
    if (phase === 'quiz') {
      setPhase('quiz-prompt');
      setQuizFeedback(null);
      return;
    }
    if (phase === 'quiz-prompt') {
      setPhase('lesson');
      setActiveTab(LESSON_TABS.length - 1);
      setVideoCollapsed(false);
      bumpVideoKey();
      return;
    }
    if (phase === 'lesson' && activeTab > 0) {
      handleBackTab();
    }
  };

  const handleSkipTab = () => {
    handleNextTab();
  };

  const replayCurrentVideo = () => {
    const video = lessonVideoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setVideoCollapsed(false);
    void video.play();
  };

  const handleEntryStart = () => {
    setFadeOut(true);
    window.setTimeout(() => {
      setPhase('lore');
      setFadeOut(false);
    }, 300);
  };

  const skipToLesson = () => {
    completeLore();
  };

  const completeLore = () => {
    loreVideoRef.current?.pause();
    setPhase('lesson');
    setActiveTab(0);
    setVideoCollapsed(false);
    bumpVideoKey();
    window.scrollTo(0, 0);
  };

  const handleBackFromLore = () => {
    loreVideoRef.current?.pause();
    setPhase('entry');
  };

  const beginLesson = () => {
    completeLore();
  };

  const handleStartOver = () => {
    setPhase('lore');
    setFadeOut(false);
    setActiveTab(0);
    setVideoKey(0);
    setVideoCollapsed(false);
    setQuizSlots([null, null, null, null, null]);
    setQuizPool([]);
    setQuizFeedback(null);
    setDraggedCard(null);
    window.scrollTo(0, 0);
  };

  const startQuiz = () => {
    const shuffled = shuffle(CORRECT_ORDER);
    setQuizPool(shuffled);
    setQuizSlots([null, null, null, null, null]);
    setQuizFeedback(null);
    setPhase('quiz');
  };

  const jumpToSection = (section: JumpSection) => {
    if (section.startsWith('tab-')) {
      const index = Number(section.replace('tab-', ''));
      setPhase('lesson');
      setActiveTab(index);
      setVideoCollapsed(false);
      bumpVideoKey();
      return;
    }

    switch (section) {
      case 'entry':
        setPhase('entry');
        break;
      case 'lore':
        setPhase('lore');
        break;
      case 'quiz-prompt':
        setPhase('quiz-prompt');
        break;
      case 'quiz':
        startQuiz();
        break;
      case 'complete':
        setPhase('complete');
        break;
    }
  };

  const getJumpValue = (): JumpSection => {
    if (phase === 'entry') return 'entry';
    if (phase === 'lore') return 'lore';
    if (phase === 'lesson') return `tab-${activeTab}`;
    if (phase === 'quiz-prompt') return 'quiz-prompt';
    if (phase === 'quiz') return 'quiz';
    return 'complete';
  };

  const getCardLabel = (id: QuizCardId) => QUIZ_CARDS.find((c) => c.id === id)?.label ?? id;

  const placeCardInSlot = (slotIndex: number, cardId: QuizCardId) => {
    setQuizSlots((prevSlots) => {
      const nextSlots = [...prevSlots];
      const fromSlot = nextSlots.findIndex((s) => s === cardId);
      const displaced = nextSlots[slotIndex];

      if (fromSlot !== -1) nextSlots[fromSlot] = null;
      nextSlots[slotIndex] = cardId;

      setQuizPool((prevPool) => {
        const pool = prevPool.filter((id) => id !== cardId);
        if (displaced && displaced !== cardId && !pool.includes(displaced)) {
          return [...pool, displaced];
        }
        return pool;
      });

      return nextSlots;
    });
    setQuizFeedback(null);
  };

  const handleDropOnSlot = (e: DragEvent<HTMLDivElement>, slotIndex: number) => {
    e.preventDefault();
    const cardId = (e.dataTransfer.getData('text/plain') || draggedCard) as QuizCardId;
    if (cardId) placeCardInSlot(slotIndex, cardId);
    setDraggedCard(null);
  };

  const handleDropOnPool = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const cardId = (e.dataTransfer.getData('text/plain') || draggedCard) as QuizCardId;
    if (!cardId) return;

    setQuizSlots((prev) => prev.map((s) => (s === cardId ? null : s)));
    setQuizPool((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
    setQuizFeedback(null);
    setDraggedCard(null);
  };

  const checkQuiz = () => {
    const filled = quizSlots.every((s) => s !== null);
    if (!filled) return;

    const correct = quizSlots.every((id, i) => id === CORRECT_ORDER[i]);
    setQuizFeedback(correct ? 'correct' : 'incorrect');
    if (correct) setPhase('complete');
  };

  const renderQuizCard = (cardId: QuizCardId, fromPool = false) => (
    <div
      key={cardId}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', cardId);
        setDraggedCard(cardId);
      }}
      onDragEnd={() => setDraggedCard(null)}
      style={{
        fontFamily: FONTS.mono,
        fontSize: '0.8rem',
        letterSpacing: '0.06em',
        color: COLORS.text,
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.borderLight}`,
        padding: '12px 20px',
        cursor: 'grab',
        boxShadow: draggedCard === cardId ? `2px 4px 8px ${COLORS.shadow}` : 'none',
        minWidth: fromPool ? '140px' : 'auto',
        textAlign: 'center',
      }}
    >
      {getCardLabel(cardId)}
    </div>
  );

  const renderTabbedLesson = () => (
    <div className="w-full max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-4 px-2" style={{ fontFamily: FONTS.mono, fontSize: '0.7rem', letterSpacing: '0.12em', color: COLORS.textMuted }}>
        <span>MODULE 01</span>
        <span>
          LESSON {activeTab + 1} / {LESSON_TABS.length}
        </span>
      </div>

      <div className="tab-bar">
        {LESSON_TABS.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            className={`tab${activeTab === index ? ' active' : ''}`}
            onClick={() => handleTabClick(index)}
          >
            [{tab.id}] {tab.label}
          </button>
        ))}
      </div>

      <div className="lesson-header">
        <h2 style={{ fontFamily: FONTS.mono, fontSize: '1rem', letterSpacing: '0.12em', margin: 0 }}>
          [{currentTab.id}] {currentTab.label.toUpperCase()}
        </h2>
        <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, margin: '0.5rem 0 0', fontSize: '0.95rem' }}>
          {currentTab.title}
        </p>
        {videoCollapsed && (
          <p style={{ fontFamily: FONTS.mono, fontSize: '0.7rem', color: COLORS.textMuted, marginTop: '0.75rem' }}>
            Video hidden — click tab [{currentTab.id}] to show
          </p>
        )}
      </div>

      <div className="video-container">
        {!videoCollapsed && (
          <video
            key={videoKey}
            ref={lessonVideoRef}
            width="100%"
            height="auto"
            controls
            preload="metadata"
            style={{ maxWidth: '800px', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)' }}
          >
            <source src={`/videos/module1/${currentTab.video}`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      <div className="controls">
        <button type="button" style={buttonStyle} onClick={handleLessonBack} disabled={phase === 'lesson' && activeTab === 0}>
          ← Back
        </button>
        <button type="button" style={buttonStyle} onClick={replayCurrentVideo} disabled={videoCollapsed}>
          Replay Video
        </button>
        <button type="button" style={buttonStyle} onClick={handleSkipTab}>
          Skip This Lesson
        </button>
        <button
          type="button"
          style={{ ...buttonStyle, ...(isLastTab ? { borderColor: COLORS.stampGreen } : {}) }}
          onClick={handleNextTab}
        >
          {isLastTab ? 'Start Quiz →' : 'Next →'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen module1" style={{ backgroundColor: COLORS.bg, color: COLORS.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&family=Crimson+Text:ital,wght@0,400;0,600&display=swap');

        .module1 button:not(:disabled):hover {
          border-color: #d0d0d0;
          background: #333333;
          box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.35);
          transform: translateY(-1px);
        }

        .module1 button:active:not(:disabled) {
          transform: translateY(0);
        }

        .module1 button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .tab-bar {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: #1a1a1a;
          border-bottom: 1px solid #5a5a5a;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .tab {
          padding: 0.75rem 1.5rem;
          background: #2a2a2a;
          color: #d0d0d0;
          border: 1px solid #5a5a5a;
          border-radius: 4px;
          cursor: pointer;
          font-family: 'Courier Prime', monospace;
          font-size: 0.75rem;
          letter-spacing: 0.06em;
          white-space: nowrap;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .tab.active {
          background: #5a5a5a;
          color: #ffffff;
          border-color: #d0d0d0;
        }

        .lesson-header {
          padding: 1.5rem 1rem 0.5rem;
          text-align: center;
        }

        .video-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 2rem 1rem;
          background: #1a1a1a;
          min-height: 500px;
        }

        .controls {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          padding: 1.5rem 1rem;
          justify-content: center;
          background: #1a1a1a;
          border-top: 1px solid #5a5a5a;
        }

        .drop-zone-active {
          border-color: #d0d0d0 !important;
          background-color: rgba(245, 241, 232, 0.08) !important;
        }

        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .animate-fade-out { animation: fadeOut 0.3s ease-out forwards; }
        .animate-fade-in { animation: fadeIn 0.4s ease-out; }

        .completion-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
          padding: 2rem 0 0;
          margin-top: 2rem;
        }

        .completion-buttons a {
          text-decoration: none;
        }
      `}</style>

      {import.meta.env.DEV && (
        <div
          className="fixed top-4 right-4 z-50 p-2"
          style={{ backgroundColor: COLORS.panel, border: `1px solid ${COLORS.borderLight}` }}
        >
          <label style={{ fontFamily: FONTS.mono, fontSize: '0.65rem', color: COLORS.textMuted, display: 'block', marginBottom: '4px' }}>
            DEV JUMP
          </label>
          <select
            value={getJumpValue()}
            onChange={(e) => jumpToSection(e.target.value as JumpSection)}
            style={{
              fontFamily: FONTS.mono,
              fontSize: '0.7rem',
              backgroundColor: COLORS.bg,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              padding: '4px 8px',
            }}
          >
            {JUMP_SECTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {phase === 'entry' && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-6 ${fadeOut ? 'animate-fade-out' : 'animate-fade-in'}`}
          style={{ backgroundColor: COLORS.bg }}
        >
          <div className="w-full max-w-lg p-8 text-center" style={{ border: `1px solid ${COLORS.borderLight}`, backgroundColor: COLORS.panel }}>
            <p style={{ fontFamily: FONTS.mono, fontSize: '0.7rem', letterSpacing: '0.2em', color: COLORS.textMuted }}>
              MODULE 01 — CLASSIFIED
            </p>
            <h1 style={{ fontFamily: FONTS.mono, fontSize: '1.75rem', letterSpacing: '0.1em', margin: '1rem 0 0.5rem' }}>
              ANATOMY OF A PAPER
            </h1>
            <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginBottom: '2rem' }}>
              Operative initiation briefing. Estimated duration: 5 minutes.
            </p>
            <button type="button" style={buttonStyle} onClick={handleEntryStart}>
              Open Classified Briefing
            </button>
          </div>
        </div>
      )}

      {phase === 'lore' && (
        <div className="min-h-screen animate-fade-in">
          <div className="w-full max-w-5xl mx-auto">
            <div className="flex justify-between items-center mb-4 px-4 pt-8" style={{ fontFamily: FONTS.mono, fontSize: '0.7rem', letterSpacing: '0.12em', color: COLORS.textMuted }}>
              <span>MODULE 01</span>
              <span>LORE TRANSMISSION — 1.5 MIN</span>
            </div>

            <div className="lesson-header">
              <h2 style={{ fontFamily: FONTS.mono, fontSize: '1rem', letterSpacing: '0.12em', margin: 0 }}>
                MISSION BRIEFING
              </h2>
              <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, margin: '0.5rem 0 0', fontSize: '0.95rem' }}>
                Watch the transmission before beginning your operative training.
              </p>
            </div>

            <div className="video-container">
              <video
                ref={loreVideoRef}
                width="100%"
                height="auto"
                controls
                preload="auto"
                style={{ maxWidth: '800px', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)' }}
              >
                <source src="/videos/module1/module1-lore.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="controls">
              <button type="button" style={buttonStyle} onClick={handleBackFromLore}>
                ← Back
              </button>
              <button type="button" style={buttonStyle} onClick={skipToLesson}>
                Skip to Lesson
              </button>
              <button type="button" style={buttonStyle} onClick={beginLesson}>
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === 'lesson' && (
        <div className="min-h-screen py-8 px-4 animate-fade-in">{renderTabbedLesson()}</div>
      )}

      {phase === 'quiz-prompt' && (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 animate-fade-in">
          <div className="w-full max-w-lg p-8 text-center" style={{ border: `1px solid ${COLORS.borderLight}`, backgroundColor: COLORS.panel }}>
            <h2 style={{ fontFamily: FONTS.mono, fontSize: '1rem', letterSpacing: '0.12em', marginBottom: '1rem' }}>
              LESSON COMPLETE
            </h2>
            <p style={{ fontFamily: FONTS.mono, color: COLORS.text, letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
              YOU NOW UNDERSTAND PAPER STRUCTURE.
            </p>
            <p style={{ fontFamily: FONTS.body, color: COLORS.textMuted, marginBottom: '1.5rem' }}>
              Ready to test your knowledge?
            </p>
            <div className="controls" style={{ borderTop: 'none', padding: 0 }}>
              <button type="button" style={buttonStyle} onClick={handleLessonBack}>
                ← Back
              </button>
              <button type="button" style={{ ...buttonStyle, borderColor: COLORS.stampGreen }} onClick={startQuiz}>
                Start Quiz
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === 'quiz' && (
        <div className="flex flex-col items-center min-h-screen p-6 pt-12 animate-fade-in">
          <div className="w-full" style={{ maxWidth: '700px' }}>
            <h2 className="text-center mb-2" style={{ fontFamily: FONTS.mono, fontSize: '1rem', letterSpacing: '0.12em' }}>
              MISSION COMPLETE? PROVE IT.
            </h2>
            <p className="text-center mb-8" style={{ fontFamily: FONTS.body, color: COLORS.textMuted }}>
              Put the paper sections in correct order. Take as long as you need.
            </p>

            <div className="space-y-3 mb-8">
              {quizSlots.map((slot, index) => (
                <div
                  key={index}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('drop-zone-active');
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('drop-zone-active')}
                  onDrop={(e) => {
                    e.currentTarget.classList.remove('drop-zone-active');
                    handleDropOnSlot(e, index);
                  }}
                  className="drop-zone flex items-center gap-4 p-3"
                  style={{
                    border: `1px dashed ${COLORS.border}`,
                    minHeight: '56px',
                    backgroundColor: 'rgba(42, 42, 42, 0.5)',
                  }}
                >
                  <span style={{ fontFamily: FONTS.mono, fontSize: '0.85rem', color: COLORS.textMuted, minWidth: '24px' }}>
                    {index + 1}.
                  </span>
                  {slot ? (
                    renderQuizCard(slot)
                  ) : (
                    <span style={{ fontFamily: FONTS.mono, fontSize: '0.75rem', color: COLORS.textMuted }}>Drop section here</span>
                  )}
                </div>
              ))}
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropOnPool}
              className="p-4 mb-8"
              style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.panel }}
            >
              <p style={{ fontFamily: FONTS.mono, fontSize: '0.7rem', letterSpacing: '0.1em', color: COLORS.textMuted, marginBottom: '0.75rem' }}>
                AVAILABLE SECTIONS
              </p>
              <div className="flex flex-wrap gap-3">
                {quizPool.map((cardId) => renderQuizCard(cardId, true))}
                {quizPool.length === 0 && (
                  <span style={{ fontFamily: FONTS.mono, fontSize: '0.75rem', color: COLORS.textMuted }}>All sections placed</span>
                )}
              </div>
            </div>

            {quizFeedback === 'incorrect' && (
              <div
                className="p-4 mb-6 text-center"
                style={{ border: `2px solid ${COLORS.stampRed}`, fontFamily: FONTS.mono, fontSize: '0.8rem' }}
              >
                <div style={{ color: COLORS.stampRed, marginBottom: '0.5rem' }}>✗ STRUCTURE UNSTABLE</div>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Sections are out of order.
                  <br />
                  Remember: Introduction first. Evidence follows. Conclusion last.
                  <br />
                  Try again.
                </p>
              </div>
            )}

            <div className="controls" style={{ borderTop: 'none' }}>
              <button type="button" style={buttonStyle} onClick={handleLessonBack}>
                ← Back
              </button>
              <button type="button" style={buttonStyle} onClick={checkQuiz} disabled={!quizSlots.every((s) => s !== null)}>
                Verify Structure
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === 'complete' && (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 animate-fade-in">
          <div className="w-full max-w-lg p-8 text-center" style={{ border: `2px solid ${COLORS.stampGreen}`, backgroundColor: COLORS.panel }}>
            <div style={{ fontFamily: FONTS.mono, color: COLORS.stampGreen, fontSize: '1rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>
              ✓ MISSION COMPLETE
            </div>
            <p style={{ fontFamily: FONTS.body, lineHeight: 1.7, marginBottom: '1.5rem' }}>
              Structure confirmed.
              <br />
              You understand how a paper is built.
              <br />
              Ready for Module 2?
            </p>
            <p style={{ fontFamily: FONTS.mono, fontSize: '0.75rem', color: COLORS.textMuted, letterSpacing: '0.08em' }}>
              MODULE 02 — COMING SOON
            </p>
            <div className="completion-buttons">
              <button type="button" style={{ ...buttonStyle, minWidth: '140px' }} onClick={handleStartOver}>
                Start Over
              </button>
              <a href="/">
                <button type="button" style={{ ...buttonStyle, minWidth: '140px' }}>
                  Return Home
                </button>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Module1;
